import { useEffect, useState } from "react";
import type { ProjectStatus } from "../../models";
import { fetchV2Schedules, fetchProjectStatus } from "../../api/clientV2";
import { onScheduleMutated } from "../../api/scheduleMutations";

/**
 * Resolves the active schedule (first in the list — the active DB version is
 * resolved server-side) and loads its project status. Refetches when
 * `activeVersion` changes (version switch → different working DB) or when any
 * schedule mutation fires (a write moved dates, so the projection may have
 * changed — see scheduleMutations / clientV2 interceptor).
 *
 * Returns null while loading or when no schedule / project data exists — the
 * chip renders nothing in that case rather than cluttering the header.
 */
export function useProjectStatus(
  activeVersion: number | null,
): ProjectStatus | null {
  const [status, setStatus] = useState<ProjectStatus | null>(null);
  const [mutationTick, setMutationTick] = useState(0);

  useEffect(() => onScheduleMutated(() => setMutationTick((t) => t + 1)), []);

  useEffect(() => {
    let cancelled = false;

    fetchV2Schedules()
      .then((schedules) => {
        if (cancelled || schedules.length === 0) return;
        return fetchProjectStatus(schedules[0].id);
      })
      .then((s) => {
        if (!cancelled && s) setStatus(s);
      })
      .catch(() => {
        /* leave status as-is — chip keeps last value / stays hidden on error */
      });

    return () => {
      cancelled = true;
    };
  }, [activeVersion, mutationTick]);

  return status;
}
